import { initialDiscountFormData } from 'containers/invoices/types';
import { selectUserDetails } from 'containers/app/appSlice';
import DiscountCalculationFeatureAnnouncement, {
  DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_CREATE_INVOICE
} from 'containers/settings/invoice-settings/components/DiscountCalculationFeatureAnnouncement';
import AddDiscountSection from './AddDiscountSection';
import { FC, useState } from 'react';
import { useAppSelector } from 'hooks';
import { FirebaseStorage } from 'services/firebase';
import AddInvoiceDiscountModal, {
  IAddInvoiceDiscountModalConfig,
  addInvoiceDiscountModalConfigInitState
} from './AddInvoiceDiscountModal';
import { DiscountFormData } from 'containers/invoices/types';
import { VoidFn } from 'types/baseTypes';

export interface AddInvoiceDiscountWrapperProps {
  className?: string;
  isAddDiscountDisabled: boolean;
  // TODO use the same model in create and edit invoice for discount
  selectedDiscount?: DiscountFormData;
  isEditInvoiceFlow?: boolean;
  lineItemsTotalCost: number;
  handleRemoveDiscount: VoidFn;
  onSaveDiscount: (arg: DiscountFormData) => void;
  isFromBillingTab?: boolean;
  isAutoFilled?: boolean;
}

const AddInvoiceDiscountWrapper: FC<AddInvoiceDiscountWrapperProps> = ({
  className = '',
  selectedDiscount = initialDiscountFormData,
  isEditInvoiceFlow = false,
  isAddDiscountDisabled = true,
  lineItemsTotalCost = 0,
  handleRemoveDiscount,
  onSaveDiscount,
  isFromBillingTab = false,
  isAutoFilled = false
}) => {
  const merchantId = useAppSelector(selectUserDetails)?.id;
  const [addInvoiceDiscountModalConfig, setAddInvoiceDiscountModalConfig] = useState<IAddInvoiceDiscountModalConfig>(
    addInvoiceDiscountModalConfigInitState
  );
  const [isCreateInvoiceAnnouncementDismissed, setIsCreateInvoiceAnnouncementDismissed] = useState(false);

  const onAddDiscount = (): void => {
    setIsCreateInvoiceAnnouncementDismissed(true);
    if (merchantId) {
      void FirebaseStorage.updateMerchantCollectionFieldValue({
        merchantId,
        collectionFieldName: DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_CREATE_INVOICE,
        value: true
      }).catch(() => undefined);
    }

    const config: IAddInvoiceDiscountModalConfig = {
      title: 'Add a discount',
      open: true,
      shouldInitializeFields: false,
      data: {
        amount: '',
        percent: '',
        isDiscountAmountSelected: selectedDiscount?.isDiscountAmountSelected ?? false
      },
      editMode: false
    };

    setAddInvoiceDiscountModalConfig(config);
  };

  const onEditDiscount = (): void => {
    const config: IAddInvoiceDiscountModalConfig = {
      title: 'Edit discount',
      open: true,
      shouldInitializeFields: true,
      data: {
        amount: selectedDiscount?.discountInCurrency ?? '',
        percent: selectedDiscount?.discountPercent ?? '',
        isDiscountAmountSelected: selectedDiscount?.isDiscountAmountSelected ?? false,
        discountTaxTiming: selectedDiscount?.discountTaxTiming
      },
      editMode: false
    };

    setAddInvoiceDiscountModalConfig(config);
  };

  const onRemoveDiscount = (): void => {
    handleRemoveDiscount();
  };

  const onCloseAddDiscountModal = (): void => {
    setAddInvoiceDiscountModalConfig(addInvoiceDiscountModalConfigInitState);
  };

  const onSaveDiscountClick = (discountData: DiscountFormData): void => {
    onSaveDiscount(discountData);
    onCloseAddDiscountModal();
  };

  const showDiscountFeatureAnnouncement =
    !isFromBillingTab && !isEditInvoiceFlow && !isCreateInvoiceAnnouncementDismissed;

  return (
    <div className={className}>
      <AddDiscountSection
        isAddDiscountDisabled={isAddDiscountDisabled}
        isEditInvoiceFlow={isEditInvoiceFlow}
        onAddDiscountClick={!isAddDiscountDisabled ? onAddDiscount : null}
        onEditDiscount={!isAddDiscountDisabled && onEditDiscount}
        onClearSelectedDiscount={onRemoveDiscount}
        selectedDiscount={selectedDiscount}
        isFromBillingTab={isFromBillingTab}
        className={isFromBillingTab ? 'border-b-0 p-3' : ''}
        isAutoFilled={isAutoFilled}
        announcementSlot={
          showDiscountFeatureAnnouncement ? (
            <DiscountCalculationFeatureAnnouncement
              dismissFirestoreField={DISCOUNT_CALCULATION_ANNOUNCEMENT_FIELD_CREATE_INVOICE}
              className="md:ml-5"
              titleOverride="New! Set discounts before or after tax"
            />
          ) : undefined
        }
      />
      {addInvoiceDiscountModalConfig.open && (
        <AddInvoiceDiscountModal
          config={addInvoiceDiscountModalConfig}
          lineItemsTotalCost={lineItemsTotalCost}
          onClose={onCloseAddDiscountModal}
          onModifyDiscountClick={onSaveDiscountClick}
          onSaveDiscountClick={onSaveDiscountClick}
        />
      )}
    </div>
  );
};

export default AddInvoiceDiscountWrapper;
